import * as Sentry from '@sentry/react'
import type { ReactNode } from 'react'

/**
 * Top-level ErrorBoundary wrapping the entire app.
 * Catches React rendering errors and sends them to Sentry.
 * When Sentry is not configured (no DSN), renders a passthrough.
 */
export function SentryErrorBoundary({ children }: { children: ReactNode }) {
  if (!import.meta.env.VITE_SENTRY_DSN) {
    return <>{children}</>
  }

  return (
    <Sentry.ErrorBoundary
      fallback={({ error, resetError }) => (
        <div className="flex min-h-svh items-center justify-center bg-background p-8">
          <div className="mx-auto max-w-md text-center">
            <div className="mx-auto mb-6 flex size-16 items-center justify-center rounded-full bg-destructive/10">
              <svg
                className="size-8 text-destructive"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"
                />
              </svg>
            </div>

            <h1 className="text-2xl font-semibold tracking-tight text-foreground">
              Something went wrong
            </h1>

            <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
              We've been notified of this error and are looking into it.
              Please try again.
            </p>

            {import.meta.env.DEV && error instanceof Error && (
              <pre className="mt-4 rounded-lg bg-destructive/5 p-4 text-left text-xs text-destructive overflow-auto max-h-48">
                {error.message}
              </pre>
            )}

            <button
              onClick={resetError}
              className="mt-6 inline-flex items-center justify-center rounded-lg bg-primary px-6 py-2.5 text-sm font-medium text-primary-foreground shadow-sm transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              Try again
            </button>
          </div>
        </div>
      )}
    >
      {children}
    </Sentry.ErrorBoundary>
  )
}
